import React, { useCallback, useEffect, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { ReactComponent as CloseIcon } from '@pkg/assets/icons/v2/close.svg';
import { CircleButton, YatSprite } from '@pkg/components';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { YatPageVersion } from '@app/client/common/structs/yatPage/yat-page';
import config from '@app/config';
import { useLockBodyScroll } from '@app/helpers';
import { useLogout } from '@app/store/actions/auth';
import { setMobileUserNavigationIsOpen, setTwitterConnectIsOpen } from '@app/store/actions/dashboard';
import { convertYatPage } from '@app/store/actions/yat-page';

import { CryptoIcon } from '../MainNavigation/icons';

import {
    CloseButtonWrapper,
    OverflowList,
    OverflowMenuAdminItem,
    OverflowMenuButton,
    OverflowMenuItem,
    OverflowMenuLabel,
    OverflowMenuSpacer,
    OverflowMenuWrapper,
} from './styles';

export const MobileUserMenu = () => {
    const theme = useTheme();
    const [translate] = useTranslation(['dashboard', 'common']);
    const isAdmin = useAppSelector((state) => state.user?.userClass?.isAdmin);
    const yatPageVersion = useAppSelector((rootState) => rootState.yatPage.yatPageVersion);
    const showConvertYatPage = useAppSelector((state) => state.featureFlags.features.showConvertYatPage);
    const mobileUserNavigationIsOpen = useAppSelector((state) => state.dashboard.mobileUserNavigationIsOpen);
    const userState = useAppSelector((rootState) => rootState.user);
    const currentEmoji = useAppSelector((rootState) => rootState.userEmojis.currentEmoji);
    const [timeoutEvent, setTimeoutEvent] = useState<NodeJS.Timeout>();

    const dispatch = useAppDispatch();
    const logout = useLogout();

    useLockBodyScroll(mobileUserNavigationIsOpen, 'visible');

    const closeSidebar = useCallback(() => {
        dispatch(setMobileUserNavigationIsOpen(false));
    }, [dispatch]);

    const signOut = () => {
        logout();
    };

    const convertCurrentYatPage = useCallback(() => {
        dispatch(convertYatPage(currentEmoji));
    }, [dispatch, currentEmoji]);

    const handleOpenTwitter = useCallback(() => {
        dispatch(setTwitterConnectIsOpen(true));
        const timeout = setTimeout(() => {
            closeSidebar();
        }, 1000);
        setTimeoutEvent(timeout);
    }, [dispatch, closeSidebar]);

    useEffect(() => {
        return () => {
            if (timeoutEvent) clearTimeout(timeoutEvent);
        };
    }, [timeoutEvent]);

    const hasTwitterData = userState?.userClass?.user?.twitter_data;

    return (
        <AnimatePresence mode="wait">
            {mobileUserNavigationIsOpen && (
                <OverflowMenuWrapper
                    id="yat-dashboard-mobile-user-nav"
                    aria-hidden={mobileUserNavigationIsOpen ? 'false' : 'true'}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ ease: theme.transitions.eases.cubic, duration: theme.transitions.speeds.fast }}
                >
                    <CloseButtonWrapper>
                        <CircleButton onClick={closeSidebar} ariaLabel={translate('close_menu')} size="large">
                            <CloseIcon aria-hidden="true" />
                        </CircleButton>
                    </CloseButtonWrapper>
                    <AnimatePresence mode="wait">
                        {mobileUserNavigationIsOpen && (
                            <OverflowList
                                initial={{
                                    translateY: 50,
                                    opacity: 0,
                                }}
                                animate={{ translateY: 0, opacity: 1 }}
                                transition={{
                                    duration: theme.transitions.speeds.normal,
                                    ease: theme.transitions.eases.cubic,
                                }}
                            >
                                <OverflowMenuLabel>{translate('common:send_receive_crypto')}</OverflowMenuLabel>
                                <OverflowMenuItem onClick={closeSidebar}>
                                    <CryptoIcon />
                                    <Link to="/paywithyat">{translate('common:address_manager')}</Link>
                                </OverflowMenuItem>
                                <OverflowMenuLabel>{translate('common:nav_security_settings')}</OverflowMenuLabel>
                                <OverflowMenuItem onClick={closeSidebar}>
                                    <Link to="/security-settings">{translate('common:nav_two_factor_auth')}</Link>
                                </OverflowMenuItem>
                                <OverflowMenuItem onClick={closeSidebar}>
                                    <Link to="/change-password">{translate('common:nav_change_password')}</Link>
                                </OverflowMenuItem>

                                <OverflowMenuItem>
                                    <OverflowMenuButton onClick={handleOpenTwitter}>
                                        {hasTwitterData
                                            ? translate('common:twitter_manage')
                                            : translate('common:twitter_connect')}
                                    </OverflowMenuButton>
                                </OverflowMenuItem>

                                <OverflowMenuSpacer />
                                <OverflowMenuItem onClick={closeSidebar}>
                                    <a href={config.supportLink} target="_blank" rel="noopener noreferrer">
                                        {translate('common:help_center')}
                                    </a>
                                </OverflowMenuItem>
                                <OverflowMenuItem onClick={closeSidebar}>
                                    <OverflowMenuButton isDestructive onClick={signOut}>
                                        {translate('common:sign_out')}
                                    </OverflowMenuButton>
                                </OverflowMenuItem>
                                <OverflowMenuSpacer />
                                {isAdmin && (
                                    <li>
                                        <ul>
                                            <OverflowMenuLabel>Admin</OverflowMenuLabel>
                                            <OverflowMenuAdminItem>
                                                <Link to="/admin/users">
                                                    <YatSprite emojiID={'💃'} />
                                                    Admin panel
                                                </Link>
                                            </OverflowMenuAdminItem>

                                            <OverflowMenuAdminItem>
                                                <a
                                                    href={config.newYatDemoLink}
                                                    rel="noopener noreferrer"
                                                    target="_blank"
                                                >
                                                    <YatSprite emojiID={'✨'} />
                                                    {translate('admin:demo_new_yat')}
                                                </a>
                                            </OverflowMenuAdminItem>
                                            <OverflowMenuAdminItem>
                                                <Link to="/admin/users">
                                                    <YatSprite emojiID={'💃'} />
                                                    {translate('admin:manage_users')}
                                                </Link>
                                            </OverflowMenuAdminItem>
                                            {(isAdmin || showConvertYatPage) &&
                                                yatPageVersion !== YatPageVersion.V2 && (
                                                    <OverflowMenuAdminItem>
                                                        <Link to={'#'} onClick={convertCurrentYatPage}>
                                                            <YatSprite emojiID={'♻️'} />
                                                            Convert to Yat Page 2.0
                                                        </Link>
                                                    </OverflowMenuAdminItem>
                                                )}
                                        </ul>
                                    </li>
                                )}
                            </OverflowList>
                        )}
                    </AnimatePresence>
                </OverflowMenuWrapper>
            )}
        </AnimatePresence>
    );
};
